// Full tenant configuration (server-side only)
interface TenantConfigFull {
  tenantId: string
  domain: string
  
  // Microsoft Graph/MSAL configuration
  msalClientId: string
  msalScopes: string[]
  
  // Azure AI Search configuration
  azureSearchEndpoint: string
  azureSearchIndexName: string
  azureSearchAdminKey: string // SENSITIVE - server only
  
  // VAPI configuration
  vapiPublicKey: string
  vapiPrivateKey: string // SENSITIVE - server only
  
  // Twilio configuration
  twilioAccountSid: string
  twilioAuthToken: string // SENSITIVE - server only
  twilioFromNumber: string
  
  // Tandem configuration
  tandemApiKey: string // SENSITIVE - server only
  
  // Branding and customization
  brandName: string
  brandLogo: string
  primaryColor: string
  secondaryColor: string
  
  // Feature flags
  features: {
    voiceCalls: boolean
    fileManagement: boolean
    azureSearch: boolean
    customBranding: boolean
  }
  
  // Rate limits and quotas
  limits: {
    maxAssistants: number
    maxChatsPerDay: number
    maxFileSize: number
    maxStorageGB: number
  }
}

// Client-safe tenant configuration (no sensitive data)
interface TenantConfig {
  tenantId: string
  domain: string
  
  // Microsoft Graph/MSAL configuration (public)
  msalClientId: string
  msalScopes: string[]
  
  // Azure AI Search configuration (public endpoints only)
  azureSearchEndpoint: string
  azureSearchIndexName: string
  azureSearchAdminKey?: string // Masked version: "****...1234"
  
  // VAPI configuration (public key only)
  vapiPublicKey: string
  vapiPrivateKey?: string // Masked version: "****...5678"
  
  // Twilio configuration (public info only)
  twilioAccountSid: string
  twilioAuthToken?: string // Masked version: "****...9012"
  twilioFromNumber: string
  
  // Tandem configuration (masked)
  tandemApiKey?: string // Masked version: "****...3456"
  
  // Branding and customization (safe for client)
  brandName: string
  brandLogo: string
  primaryColor: string
  secondaryColor: string
  
  // Feature flags (safe for client)
  features: {
    voiceCalls: boolean
    fileManagement: boolean
    azureSearch: boolean
    customBranding: boolean
  }
  
  // Rate limits and quotas
  limits: {
    maxAssistants: number
    maxChatsPerDay: number
    maxFileSize: number
    maxStorageGB: number
  }
}

/**
 * Utility function to mask sensitive values for client-side exposure
 */
const maskSensitiveValue = (value: string | undefined): string | undefined => {
  if (!value || value.length < 8) return undefined
  
  // Show first 4 chars as asterisks and last 4 chars
  const masked = '****...' + value.slice(-4)
  return masked
}

/**
 * Convert full server config to client-safe config
 */
const sanitizeConfigForClient = (fullConfig: TenantConfigFull): TenantConfig => {
  return {
    tenantId: fullConfig.tenantId,
    domain: fullConfig.domain,
    
    // Microsoft Graph/MSAL (public data only)
    msalClientId: fullConfig.msalClientId,
    msalScopes: fullConfig.msalScopes,
    
    // Azure AI Search (mask admin key)
    azureSearchEndpoint: fullConfig.azureSearchEndpoint,
    azureSearchIndexName: fullConfig.azureSearchIndexName,
    azureSearchAdminKey: maskSensitiveValue(fullConfig.azureSearchAdminKey),
    
    // VAPI (public key only, mask private key)
    vapiPublicKey: fullConfig.vapiPublicKey,
    vapiPrivateKey: maskSensitiveValue(fullConfig.vapiPrivateKey),
    
    // Twilio (mask auth token)
    twilioAccountSid: fullConfig.twilioAccountSid,
    twilioAuthToken: maskSensitiveValue(fullConfig.twilioAuthToken),
    twilioFromNumber: fullConfig.twilioFromNumber,
    
    // Tandem (mask API key)
    tandemApiKey: maskSensitiveValue(fullConfig.tandemApiKey),
    
    // Safe client data
    brandName: fullConfig.brandName,
    brandLogo: fullConfig.brandLogo,
    primaryColor: fullConfig.primaryColor,
    secondaryColor: fullConfig.secondaryColor,
    features: fullConfig.features,
    limits: fullConfig.limits
  }
}

export const useTenantConfig = () => {
  const tenantConfig = ref<TenantConfig | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  
  /**
   * Get tenant configuration by domain or tenant ID
   * Returns client-safe configuration with sensitive values masked
   */
  const getTenantConfig = async (identifier: string): Promise<TenantConfig | null> => {
    try {
      loading.value = true
      error.value = null
      
      const response = await $fetch(`/api/tenants/config`, {
        query: { identifier }
      })
      
      if (response.success) {
        // The API should already return sanitized config, but double-check
        const sanitizedConfig = response.config.azureSearchAdminKey?.includes('****') 
          ? response.config // Already sanitized by server
          : sanitizeConfigForClient(response.config) // Sanitize if needed
        
        tenantConfig.value = sanitizedConfig
        return sanitizedConfig
      } else {
        throw new Error(response.message || 'Failed to load tenant configuration')
      }
      
    } catch (err) {
      error.value = 'Failed to load tenant configuration'
      console.error('Tenant config error:', err)
      return null
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Get tenant configuration from current domain
   */
  const getCurrentTenantConfig = async (): Promise<TenantConfig | null> => {
    if (process.client) {
      const domain = window.location.hostname
      return await getTenantConfig(domain)
    }
    return null
  }
  
  /**
   * Update tenant configuration (admin only)
   */
  const updateTenantConfig = async (config: Partial<TenantConfig>): Promise<boolean> => {
    try {
      loading.value = true
      error.value = null
      
      const response = await $fetch(`/api/tenants/config`, {
        method: 'PUT',
        body: config
      })
      
      if (response.success) {
        tenantConfig.value = { ...tenantConfig.value, ...config } as TenantConfig
        return true
      } else {
        throw new Error(response.message || 'Failed to update tenant configuration')
      }
      
    } catch (err) {
      error.value = 'Failed to update tenant configuration'
      console.error('Tenant config update error:', err)
      return false
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Check if a feature is enabled for the current tenant
   */
  const isFeatureEnabled = (feature: keyof TenantConfig['features']): boolean => {
    return tenantConfig.value?.features[feature] || false
  }
  
  /**
   * Get rate limit for a specific resource
   */
  const getRateLimit = (limit: keyof TenantConfig['limits']): number => {
    return tenantConfig.value?.limits[limit] || 0
  }
  
  /**
   * Get branded configuration for UI
   */
  const getBrandConfig = () => {
    if (!tenantConfig.value) return null
    
    return {
      name: tenantConfig.value.brandName,
      logo: tenantConfig.value.brandLogo,
      colors: {
        primary: tenantConfig.value.primaryColor,
        secondary: tenantConfig.value.secondaryColor
      }
    }
  }
  
  return {
    tenantConfig: readonly(tenantConfig),
    loading: readonly(loading),
    error: readonly(error),
    getTenantConfig,
    getCurrentTenantConfig,
    updateTenantConfig,
    isFeatureEnabled,
    getRateLimit,
    getBrandConfig
  }
}
